import React from 'react';
import { ArrowUpRight, ListFilter } from 'lucide-react';

import { cn } from '../../utils';
import type { DataTableFilter } from './data-table';
import type { FilterOption } from './column-filter-menu';
import { Skeleton } from './skeleton';
import { interceptPlainClick } from './intercept-plain-click';

/**
 * A page-level finding. It is the answer the page wants a reader to leave with,
 * not another container for arbitrary content. `brand` is reserved for a single
 * flagship conclusion; `verified` identifies evidence KnitKnot observed at the
 * edge. Both preserve a visible text label so meaning never depends on colour.
 */
export function AnalyticalConclusion({
  eyebrow,
  title,
  description,
  meta,
  action,
  tone = 'neutral',
  measure = 'default',
  className,
}: {
  eyebrow: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  meta?: React.ReactNode;
  action?: React.ReactNode;
  tone?: 'neutral' | 'brand' | 'verified';
  measure?: 'default' | 'wide';
  className?: string;
}) {
  return (
    <section
      className={cn(
        'relative overflow-hidden border px-5 py-6 sm:px-7 sm:py-7',
        tone === 'neutral' && 'border-border bg-card',
        tone === 'brand' && 'border-sidebar-border bg-sidebar text-sidebar-foreground',
        tone === 'verified' && 'border-secondary/25 bg-secondary/[0.06]',
        className,
      )}
    >
      <div
        className={cn('absolute inset-y-0 left-0 w-1', tone === 'verified' ? 'bg-secondary' : 'bg-primary')}
        aria-hidden
      />
      <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className={cn('min-w-0', measure === 'wide' ? 'max-w-6xl' : 'max-w-4xl')}>
          <p
            className={cn(
              'text-detail font-semibold uppercase tracking-[0.14em]',
              tone === 'brand' ? 'text-primary' : tone === 'verified' ? 'text-secondary' : 'text-primary',
            )}
          >
            {eyebrow}
          </p>
          <div
            className={cn(
              'mt-2 text-2xl font-semibold leading-[1.16] tracking-[-0.035em] sm:text-[2rem]',
              tone === 'brand' ? 'text-sidebar-foreground' : 'text-foreground',
            )}
          >
            {title}
          </div>
          {description && (
            <div
              className={cn(
                'mt-3 text-sm leading-6',
                measure === 'wide' ? 'max-w-6xl' : 'max-w-3xl',
                tone === 'brand' ? 'text-sidebar-foreground/70' : 'text-ink-supporting',
              )}
            >
              {description}
            </div>
          )}
          {meta && (
            <div
              className={cn('mt-4 text-xs', tone === 'brand' ? 'text-sidebar-foreground/55' : 'text-muted-foreground')}
            >
              {meta}
            </div>
          )}
        </div>
        {action && <div className="w-full shrink-0 lg:w-auto">{action}</div>}
      </div>
    </section>
  );
}

/** One measured value and the comparison or qualifier needed to interpret it. */
export function MetricComparison({
  label,
  value,
  comparison,
  emphasis = 'default',
  onClick,
  href,
  active = false,
  interaction = 'metric',
  reserveComparison = false,
  className,
  containerClassName,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  comparison?: React.ReactNode;
  emphasis?: 'default' | 'verified' | 'quiet';
  href?: string;
  onClick?: () => void;
  active?: boolean;
  /** The control contract, not a visual variant. Filters narrow the table below,
   *  navigation opens another surface, and metrics are read-only. */
  interaction?: 'metric' | 'filter' | 'navigate';
  /**
   * Keep the qualifier row even when THIS cell has no `comparison`, because a
   * SIBLING in the same ledger does.
   *
   * The row is 32px, and the label slot above it stretches to fill whatever
   * height the row has spare. So in a ledger where only some cells carry a
   * qualifier, the ones without it hand those 32px straight to their label slot
   * and drop their label AND value a full row below their neighbours' — which
   * is what made the source-detail headers read as unaligned (Max, 2026-08-31).
   *
   * `MetricLedger` sets it from its own items, so the reservation is one
   * decision per ledger rather than a convention each caller has to remember.
   * A ledger with no qualifiers anywhere still reserves nothing.
   */
  reserveComparison?: boolean;
  className?: string;
  containerClassName?: string;
}) {
  // `-ml-3.5` is NOT a nudge — it is the second half of the reservation trick
  // documented on the label span below. The span reserves 16px of inline
  // padding-right so the line breaker always leaves room on the LAST line, and
  // the glyph is then pulled back into that reserved space.
  const glyph =
    interaction === 'filter' ? (
      <ListFilter className="-ml-3.5 inline-block h-3 w-3 align-[-1px] text-primary/70" aria-label="Table filter" />
    ) : interaction === 'navigate' ? (
      <ArrowUpRight className="-ml-3.5 inline-block h-3 w-3 align-[-1px] text-ink-tertiary" aria-label="Opens detail" />
    ) : null;
  // EVERY cell reserves the 2px selection rule as transparent, whatever its
  // interaction. Only a filter can ever paint it — but the 2px is part of the
  // cell's HEIGHT, and the label slot stretches to fill the row, so reserving it
  // on some cells and not others made those labels 2px taller and knocked their
  // values off the shared baseline (measured 291px vs 293px on /claims). Equal
  // reserve, equal height. It is also what keeps SELECTING a metric from
  // shifting the band (rule 67).
  const cellFrame = cn('border-b-2 border-b-transparent', containerClassName);
  const content = (
    <dl
      className={cn(
        // ALL metric dividers are the neutral hairline — no coloured border on
        // "verified"/owned metrics (Max, 2026-08-24: a teal rule between
        // Citations and Owned citations read as a stray divider, not emphasis).
        // NO FILL, in any state (Max, 2026-08-30) — see the button below.
        // A flex COLUMN so the label slot can absorb the row's slack; `h-full`
        // is what makes the cell stretch to the tallest one (see the `<dt>`).
        'flex min-w-0 flex-col border-l border-border pl-3.5',
        className,
      )}
    >
      {/*
        The label slot is flex ONLY to bottom-align, and it holds exactly ONE
        inline child. Both halves matter and both were bugs when this was a
        plain `flex items-center gap-1.5` row (2026-08-30):

        1. HEIGHT-LOCKED + BOTTOM-ALIGNED. The track is narrow enough (8.5rem)
           that most labels wrap to two lines, and a one-line label beside a
           two-line one put their VALUES on different baselines — the row read
           as ragged even though every cell was the same width. Reserving two
           lines and growing upward means "104", "0" and "12" always align.
        2. ONE inline child, never sibling flex items. Flex makes each text node
           its own anonymous item and COLLAPSES the whitespace between them, so
           `Buyer roles` rendered as `BUYERROLES`. All label content — the
           caller's node and the glyph — lives in one `<span>`.

        The glyph follows the label with NO whitespace between them, which is
        what stops it orphaning onto a line of its own: a line break needs a
        break opportunity, and adjacent inline runs with no space between them
        offer none. That works for a plain string AND for the `TooltipHeader`
        node most callers pass, which is why there is no last-word splitting
        here — CSS does it for every label shape.
      */}
      {/* `flex-1` + bottom alignment, NOT a fixed two-line reserve.

          The reserve was unconditional, so a ledger whose labels all fit one
          line still paid 14px of dead space above every one of them and the
          whole band sat taller than it needed to (Max, 2026-08-30, on the
          review queue: six one-word labels with a blank line above each).

          Stretch does the same job for free and only when it is needed. The
          cells are flex items in a row, so they are all as tall as the tallest;
          inside a cell the label slot takes the leftover space and pins its text
          to the BOTTOM. Nothing wraps ⇒ no cell is taller ⇒ no leftover space ⇒
          the band is tight. One label wraps ⇒ every other label's slot grows by
          exactly one line and their values stay on the shared baseline. The
          alignment guarantee is unchanged; what goes away is paying for it when
          there is nothing to align. */}
      <dt className="flex flex-1 items-end">
        <span
          className={cn(
            // Sentence case, not uppercase (Max, 2026-08-30). Caps run ~15%
            // wider and need tracking on top, which is width a 136px track does
            // not have; dropping them buys the label 9.5px → 11px. It also puts
            // the ledger back inside design rule 46, which reserves uppercase
            // for the ONE masthead eyebrow on the hosted report and says a
            // small size already signals "supporting label" without shouting.
            'block text-detail font-medium leading-[1.3] text-ink-supporting',
            active && interaction === 'filter' && 'text-foreground',
          )}
        >
          {/*
            THE GLYPH RESERVATION. `pr-4` here plus `-ml-3.5` on the glyph is
            what guarantees the funnel never lands on a line of its own (Max,
            2026-08-30: *"i never want the filter icon on a different line than
            the field name"*).

            Inline `padding-right` is applied to the END of an inline box's LAST
            line fragment and PARTICIPATES IN LINE BREAKING, so reserving 16px
            makes the breaker treat the last word as 16px wider than it is and
            break one word earlier when it has to. The glyph is then pulled back
            into that reserved space with an equal negative margin, so it costs
            no layout width and sits ~2px after the text.

            Two simpler things were tried in the browser and BOTH FAILED — do not
            re-attempt them:
              · Deleting the whitespace between label and glyph. An SVG is an
                ATOMIC INLINE and CSS gives one a soft-wrap opportunity on both
                sides whether or not a space is there.
              · A U+2060 WORD JOINER between them. Chrome breaks before an atomic
                inline anyway; measured identical to no joiner at all.
            Both left "Protection priorities" on one line with the funnel alone
            on a second, while the labels that already wrapped looked correct —
            which is exactly why this needs a browser check and not a unit test.

            It also works for any label SHAPE, which the alternative (splitting
            the last word into a `whitespace-nowrap` span) does not: most callers
            pass a `TooltipHeader` node, and a node has no last word to split.
          */}
          <span className={cn(glyph && 'pr-4')}>{label}</span>
          {glyph}
        </span>
      </dt>
      {/* Value and qualifier rows are height-LOCKED for the same reason the
          label slot is: a long number or a long qualifier must not be able to
          grow one cell and shift the row.
          The qualifier reserves TWO lines. One was the plan — a second line
          costs ~16px of band height — but the 136px track holds only ~19
          characters per line, and an audit of all 91 live qualifiers found 73
          of them longer than that. Buying 16px by truncating four fifths of the
          product's qualifiers is the wrong trade: the qualifier exists to state
          a denominator, and "contain the same claim in 2+ distinct sourc…" does
          not. Two lines hold ~38 characters, which covers all but a handful
          (those were shortened). It stays CLAMPED and height-locked, so the
          band is still exactly one height everywhere. */}
      <dd
        className={cn(
          'mt-1 flex items-end overflow-hidden font-semibold tabular-nums tracking-[-0.025em] text-foreground',
          emphasis === 'quiet' ? 'h-7 text-lg' : 'h-8 text-2xl',
        )}
      >
        <span className="truncate">{value}</span>
      </dd>
      {(comparison != null || reserveComparison) && (
        <dd
          className="mt-1 line-clamp-2 h-8 text-xs leading-4 text-muted-foreground"
          title={typeof comparison === 'string' ? comparison : undefined}
        >
          {comparison}
        </dd>
      )}
    </dl>
  );
  if (href) {
    return (
      <a
        href={href}
        onClick={(event) => interceptPlainClick(event, onClick)}
        data-metric-interaction={interaction}
        className={cn('focus-ring block rounded-sm text-left hover:bg-surface-hover', cellFrame)}
      >
        {content}
      </a>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={interaction === 'filter' ? active : undefined}
        data-metric-interaction={interaction}
        className={cn(
          // NOT `w-full`: inside the flex metrics row that would force the cell
          // to the full container width and stack the metrics (and pinning a
          // basis to stop that then capped every cell at 9.5rem, wrapping the
          // footer). `block` alone sizes the cell to its content like the `<a>`.
          'focus-ring block rounded-sm text-left hover:bg-surface-hover',
          // A FILTER METRIC CARRIES NO FILL — not at rest, not when selected
          // (Max, 2026-08-30: *"they should not have a different background
          // color like they do now — that is too distracting, they should just
          // have that little filter icon, that is good enough. And if it is
          // selected, it should have that underlined border, but no background
          // color."*). Three tints were in play — `bg-primary/[0.025]` at rest,
          // `/[0.07]` selected, and a third `/[0.06]` on the `<dl>` — and
          // together they made the selected cell read as a flagged alert rather
          // than a control. Hover keeps `bg-surface-hover`, the same wash a
          // table row uses; that is the ONE background left.
          //
          // `cellFrame` FIRST: it carries the transparent reserve, and
          // tailwind-merge keeps the LAST conflicting utility — with the order
          // reversed, the reserve silently overwrote the selected rule and a
          // selected filter rendered unmarked.
          cellFrame,
          // Selection is two channels — the rule plus the label going to
          // `text-foreground` — so it never depends on colour alone.
          interaction === 'filter' && active && 'border-b-primary',
        )}
      >
        {content}
      </button>
    );
  }
  return <div className={cellFrame}>{content}</div>;
}

export interface MetricLedgerItem {
  key: string;
  label: React.ReactNode;
  value: React.ReactNode;
  comparison?: React.ReactNode;
  emphasis?: 'default' | 'verified' | 'quiet';
  href?: string;
  onClick?: () => void;
  active?: boolean;
  /** Explicitly distinguishes a table quick-filter from a drill/navigation
   *  action. Omit for read-only metrics; legacy interactive items are inferred
   *  from `active` (filter) or `href`/`onClick` (navigate). */
  interaction?: 'metric' | 'filter' | 'navigate';
}

/**
 * A compact page-level evidence row. It replaces detached KPI-card grids on
 * operational and analytical lists: every measure owns an equal ledger track,
 * comparison text stays attached, and the strip collapses without changing
 * the surrounding page geometry.
 */
export function MetricLedger({
  // Optional because `loading` REPLACES it — a caller in its loading state has
  // no items yet, and requiring an empty array there is a lie the type forces
  // every skeleton to tell.
  items = [],
  ariaLabel,
  lead,
  context,
  actions,
  loading = false,
  loadingCount = 4,
  className,
}: {
  items?: MetricLedgerItem[];
  ariaLabel: string;
  /** Optional identity for a DETAIL page — the entity the metrics describe
   *  (a `SourceIdentity`, a `CompanyMark` + name, a competitor/keyword name).
   *  Renders at the TOP of the left column, above `context`, so a detail
   *  header reads identity → per-tab explanation → metrics without forking a
   *  second header component. List/hub pages omit it (they have no single
   *  entity). */
  lead?: React.ReactNode;
  /** Optional operational context that qualifies the metrics: dates, scope,
   *  provider, current status, or (on a detail page) a one-line explanation of
   *  what the active tab shows. The page header already owns purpose copy, so
   *  this slot must not repeat the page title. */
  context?: React.ReactNode;
  /** Controls that act on the summary's scope. Kept with context rather than
   *  floating at the far edge of the metric row. */
  actions?: React.ReactNode;
  loading?: boolean;
  loadingCount?: number;
  className?: string;
}) {
  // `Skeleton` is the ONE pulse primitive (rule 59) — these were hand-rolled
  // `animate-pulse bg-muted` spans, which `check-canonical` CHECK 15 bans. They
  // survived only because that check scans `apps/console/src` and never looked
  // in `packages/shared`, which is where the canonical components live.
  const sourceItems: MetricLedgerItem[] = loading
    ? Array.from({ length: loadingCount }, (_, index) => ({
        key: `loading-${index}`,
        label: <Skeleton className="block h-2.5 w-20" />,
        value: <Skeleton className="mt-2 block h-6 w-14" />,
      }))
    : items;
  const interactionFor = (item: MetricLedgerItem): NonNullable<MetricLedgerItem['interaction']> =>
    item.interaction ?? (item.active !== undefined ? 'filter' : item.href || item.onClick ? 'navigate' : 'metric');
  // One stable rule across every page: table filters lead, navigation follows,
  // and informational measures finish the row. This keeps the interactive
  // portion predictable without asking each caller to hand-sort mixed tiles.
  const interactionOrder = { filter: 0, navigate: 1, metric: 2 } as const;
  const renderedItems = sourceItems
    .map((item, index) => ({ item, index, interaction: interactionFor(item) }))
    .sort((a, b) => interactionOrder[a.interaction] - interactionOrder[b.interaction] || a.index - b.index);
  const hasLeftColumn = lead != null || context != null || actions != null;
  // ONE decision per ledger: if any cell carries a qualifier, every cell keeps
  // the row. Mixed ledgers were the alignment bug — see `reserveComparison`.
  // The loading skeleton opts out (its ghosts carry no qualifier and there is
  // nothing to align against).
  const reserveComparison = !loading && renderedItems.some(({ item }) => item.comparison != null);

  return (
    <section
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      className={cn('border-y border-border bg-card', className)}
    >
      {/* Pure flexbox, ZERO grid `auto`/`auto-fit` cleverness — the metrics are
          ALWAYS a single ROW (Max, 2026-08-24, after grid `auto-fit`/`auto`-track
          approaches stacked on Coverage/Access). With a left column the card is
          `flex-col xl:flex-row`: context and metrics share the desktop row, while
          each metric owns a FIXED track rather than the `w-full` applied by an
          interactive cell. That track is the critical part: without it clickable
          metrics stack even inside a flex container. Below `xl` the context sits
          above a full-width metrics row. Without a left column the ledger remains
          a stretching auto-fit grid. */}
      <div className={cn('flex flex-col', hasLeftColumn && 'xl:flex-row')}>
        {hasLeftColumn && (
          // 22rem, not 15rem: the description is the reader's orientation and it
          // gets the LARGER share of the band (Max, 2026-08-29 — *"i want the
          // description on the left to generally have more width than the
          // metrics"*). With a 8.5rem metric track, five metrics leave the
          // sentence the bigger half at every console width; the floor is what
          // makes that a guarantee rather than an average.
          <div className="flex min-w-0 flex-col gap-3 border-b border-border px-4 py-4 sm:px-5 xl:min-w-[22rem] xl:flex-1 xl:justify-center xl:border-b-0">
            {lead && <div className="min-w-0">{lead}</div>}
            {(context || actions) && (
              <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                {context && <div className="min-w-0 text-sm leading-5 text-ink-supporting">{context}</div>}
                {actions && <div className="shrink-0">{actions}</div>}
              </div>
            )}
          </div>
        )}
        <div
          className={cn(
            // THE METRICS ARE ONE ROW AND NEVER WRAP (Max, 2026-08-29: *"i do
            // not like the look of stacking metrics i want to avoid that at all
            // costs, we should just make the metrics widths smaller in this case
            // so everything can fit on one row"*). A wrapped ledger reads as a
            // GRID OF CARDS, which is a different component from the one-line
            // strip — shipping both shapes is the inconsistency this whole pass
            // exists to remove. Pressure is absorbed by the track shrinking
            // toward its floor, never by a second row; if even the floor cannot
            // fit beside a 22rem description, the `xl:flex-row` breakpoint has
            // already stacked the whole band, which is a different layout rather
            // than a broken one.
            hasLeftColumn
              ? 'flex min-w-0 flex-nowrap'
              : 'grid [grid-template-columns:repeat(auto-fit,minmax(8.5rem,1fr))]',
          )}
        >
          {renderedItems.map(({ item, interaction }, index) => (
            <MetricComparison
              key={item.key}
              label={item.label}
              value={item.value}
              comparison={item.comparison}
              emphasis={item.emphasis}
              href={item.href}
              onClick={item.onClick}
              active={item.active}
              interaction={interaction}
              reserveComparison={reserveComparison}
              containerClassName={cn(
                // ONE FIXED TRACK — 8.5rem (136px), shrinking to a 7rem floor,
                // and it never GROWS. Content-sizing was the original defect
                // (Max, 2026-08-29): a cell whose qualifier read "owned evidence
                // with zero or one mapped answer" was wide and a cell reading
                // "17" was narrow, so the same component rendered visibly
                // different furniture on every page and a two-metric ledger did
                // not look like a smaller version of a six-metric one — it
                // looked like a different design. A fixed track makes cell width
                // a property of the SYSTEM rather than of the copy.
                //
                // 136px is deliberately narrow enough that most labels wrap to
                // two lines; that is fine (the label slot reserves both and
                // bottom-aligns, so values stay on one baseline) and it is what
                // keeps the description the larger half.
                hasLeftColumn && 'w-[8.5rem] min-w-[7rem] shrink',
                // odd-last-item full-width only matters in the full-bleed GRID
                // (its mobile 2-col wrap); a flex-wrap row needs no span.
                !hasLeftColumn &&
                  renderedItems.length % 2 === 1 &&
                  index === renderedItems.length - 1 &&
                  'col-span-full sm:col-span-1',
              )}
              className={cn(
                // p-3.5 (14px), the canonical card padding floor — px-4/sm:px-5
                // spent 32–40px of a 136px track on gutters.
                'h-full px-3.5 py-3.5',
                index === 0 && (hasLeftColumn ? 'border-l-0 xl:border-l' : 'border-l-0'),
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * A categorical table facet presented as an at-a-glance count ledger. Each
 * metric applies one value to the SAME filter used by the table column, so the
 * summary can never drift into a second filtering system.
 * These are mutually exclusive quick filters: a new metric replaces the
 * previous selection and clicking the active metric clears it. The full filter
 * column menu may still offer deliberate multi-select. Zero-count and countless
 * opt-in options stay out of the summary.
 */
export function nextFacetMetricSelection(selected: readonly string[], value: string): string[] {
  return selected.length === 1 && selected[0] === value ? [] : [value];
}

export function FacetMetricLedger({
  ariaLabel,
  context,
  filter,
  order,
  label,
  loading = false,
  loadingCount = 4,
  className,
}: {
  ariaLabel: string;
  context?: React.ReactNode;
  filter: DataTableFilter;
  order?: readonly string[];
  label?: (option: FilterOption) => React.ReactNode;
  loading?: boolean;
  loadingCount?: number;
  className?: string;
}) {
  const orderByValue = new Map((order ?? []).map((value, index) => [value, index]));
  const options = filter.options
    .filter((option) => option.count != null && option.count > 0)
    .map((option, index) => ({ option, index }))
    .sort((a, b) => {
      const aOrder = orderByValue.get(a.option.value);
      const bOrder = orderByValue.get(b.option.value);
      if (aOrder != null || bOrder != null)
        return (aOrder ?? Number.MAX_SAFE_INTEGER) - (bOrder ?? Number.MAX_SAFE_INTEGER);
      return a.index - b.index;
    })
    .map(({ option }) => option);

  return (
    <MetricLedger
      ariaLabel={ariaLabel}
      context={context}
      items={options.map((option) => {
        const active = filter.selected.length === 1 && filter.selected[0] === option.value;
        return {
          key: option.value,
          label: label ? label(option) : option.label,
          value: option.count!.toLocaleString(),
          active,
          interaction: 'filter' as const,
          onClick: () => filter.onChange(nextFacetMetricSelection(filter.selected, option.value)),
        };
      })}
      loading={loading}
      loadingCount={loadingCount}
      className={className}
    />
  );
}

/**
 * The canonical analytical plot frame. It names the question/conclusion above
 * the chart and keeps legends/controls with the visualization they affect.
 */
export function ChartFrame({
  title,
  conclusion,
  meta,
  controls,
  children,
  className,
}: {
  title: string;
  conclusion?: React.ReactNode;
  meta?: React.ReactNode;
  controls?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    // A FULL-BLEED BAND (`border-y`), not an all-round card. Every one of the
    // six call sites renders this in a vertical page stack, and on three of them
    // a `MetricLedger` — which is `border-y` — sits directly above it, so the
    // all-round box made one page read as two different materials stacked
    // (Max, 2026-08-30, on /overview: *"I don't really like how many
    // container/border inconsistencies we have"*). Padding matches the ledger's
    // for the same reason.
    <section className={cn('min-w-0 border-y border-border bg-card', className)} aria-label={title}>
      <header className="flex flex-col gap-3 border-b border-border px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:px-5">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          {conclusion && <div className="mt-1 text-sm leading-5 text-ink-content">{conclusion}</div>}
          {meta && <div className="mt-1 text-xs text-muted-foreground">{meta}</div>}
        </div>
        {controls && <div className="shrink-0">{controls}</div>}
      </header>
      <div className="min-w-0 p-4 sm:p-5">{children}</div>
    </section>
  );
}

/**
 * Owns a table's title, evidence summary, filters, and count. The DataTable
 * remains the only component that paints table chrome; this frame deliberately
 * adds no second rounded card around it.
 */
export function TableFrame({
  title,
  description,
  count,
  action,
  toolbar,
  children,
  className,
}: {
  title?: React.ReactNode;
  description?: React.ReactNode;
  count?: React.ReactNode;
  action?: React.ReactNode;
  toolbar?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const hasHeader = title || description || count || action;
  return (
    <section className={cn('min-w-0', className)}>
      {hasHeader && (
        <header className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              {title && <h2 className="text-base font-semibold text-foreground">{title}</h2>}
              {count && <span className="text-xs tabular-nums text-muted-foreground">{count}</span>}
            </div>
            {description && <div className="mt-1 max-w-3xl text-sm leading-5 text-ink-supporting">{description}</div>}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </header>
      )}
      {toolbar && <div className="mb-3">{toolbar}</div>}
      {children}
    </section>
  );
}

/**
 * A settings row with explanatory hierarchy. The left side explains scope and
 * consequence; the right side contains the controls or current configuration.
 */
export function ConfigurationSection({
  title,
  description,
  children,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        'grid gap-4 border-t border-border py-6 first:border-t-0 first:pt-0 xl:grid-cols-[minmax(11rem,15rem)_minmax(0,1fr)] xl:gap-8',
        className,
      )}
    >
      <header>
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {description && <div className="mt-1 text-sm leading-5 text-muted-foreground">{description}</div>}
      </header>
      <div className="min-w-0">{children}</div>
    </section>
  );
}
